import {
  ChartContainer,
  ChartGrid,
  ChartTooltip,
  Scatter,
  XAxis,
  YAxis,
  tokens,
  Stack,
  Typography,
} from '@cast/design-system';

import type { WorkloadScatterPoint } from '../_hooks/useReliabilityDashboardData';

type ScatterMetric = 'errorRatePercent' | 'errorRate' | 'latencyP95Ms';

type Props = {
  data: WorkloadScatterPoint[];
  metric: ScatterMetric;
  isLoading?: boolean;
};

const HEALTH_COLORS: Record<
  WorkloadScatterPoint['health'],
  'green.500' | 'yellow.500' | 'red.500' | 'grey.300'
> = {
  healthy: 'green.500',
  warning: 'yellow.500',
  breach: 'red.500',
  unknown: 'grey.300',
};

const METRIC_LABELS: Record<ScatterMetric, string> = {
  errorRatePercent: 'Error rate (%)',
  errorRate: 'Errors/s',
  latencyP95Ms: 'P95 latency (ms)',
};

const formatMetricValue = (metric: ScatterMetric, value: number): string => {
  if (metric === 'latencyP95Ms') return `${value.toFixed(0)}ms`;
  if (metric === 'errorRatePercent') return `${value.toFixed(2)}%`;
  return `${value.toFixed(3)}/s`;
};

export const ReliabilityScatterChart = ({ data, metric, isLoading }: Props) => (
  <Stack flex={1} minHeight={200}>
    <ChartContainer<WorkloadScatterPoint>
      data={data}
      isLoading={isLoading}
      sx={{ width: '100%', height: '100%' }}
      margin={{ top: 8, bottom: 24, left: 0, right: 0 }}
      testId="reliability-scatter-chart"
    >
      <ChartGrid hideVertical />
      <Scatter<WorkloadScatterPoint>
        dataKey={metric}
        shapeProps={(payload) => ({
          shapeColor: payload ? HEALTH_COLORS[payload.health] : 'grey.300',
          shapeSize: 'small',
        })}
        hideActiveShape
      />
      <XAxis<WorkloadScatterPoint>
        dataKey="requestRate"
        type="number"
        tick={{ color: tokens.theme.fg.structural.secondary }}
        tickFormatter={(v) => Number(v).toFixed(1)}
      />
      <YAxis<WorkloadScatterPoint>
        tickFormatter={(v) =>
          metric === 'latencyP95Ms'
            ? Number(v).toFixed(0)
            : metric === 'errorRatePercent'
            ? `${Number(v).toFixed(1)}%`
            : Number(v).toFixed(2)
        }
      />
      <ChartTooltip<WorkloadScatterPoint>
        testId="reliability-scatter-tooltip"
        TooltipComponent={({ payload }) => {
          if (!payload?.length) return null;
          const pt = payload[0]?.payload;
          if (!pt) return null;
          return (
            <Stack
              sx={{
                background: tokens.theme.bg.structural.surface,
                border: `1px solid ${tokens.theme.border.structural.neutral}`,
                borderRadius: tokens.theme.radius.sm,
                p: 8,
                gap: 4,
              }}
            >
              <Typography variant="P12B">{pt.name}</Typography>
              <Typography variant="P12R">
                Req/s: {pt.requestRate.toFixed(3)}
              </Typography>
              <Typography variant="P12R">
                {METRIC_LABELS[metric]}: {formatMetricValue(metric, pt[metric])}
              </Typography>
            </Stack>
          );
        }}
      />
    </ChartContainer>
  </Stack>
);
